import { useEffect, useContext, useMemo, useCallback } from 'react'
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined'
import { CircularProgress, Fab } from '@mui/material'
import { groupBy } from 'lodash'
import { useSelector } from 'react-redux'
import { Redirect, useLocation, useHistory } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import {
  MotionDivContainer,
  PendingRowTypeDisplayText,
  pendingStyles,
  PendingTableRow,
  PendingTypeOptions,
} from '@components'
import { CvsTable } from '@components/table/cvs-table'
import { PendingContext, PendingContextProvider, submissionContext } from '@contexts'
import { IPendingDatasetGlobalResponse, IGlobalPendingRollupResponse } from '@global/api/types'
import { useGlobalPendingSubmissions } from '@templates/hooks/useQueryPending'
import { getColumns } from './config'
import { Toolbar } from './toolbar'

type IPendingSubmissions = (IPendingDatasetGlobalResponse | IGlobalPendingRollupResponse)[]

const STATUS_FILTER_OPTIONS = Object.entries(PendingTypeOptions).map(([value, label]) => ({
  label,
  value,
}))

interface FilterPendingParams {
  selectedStatusFilters: PendingRowTypeDisplayText[]
  template_id: string | null
  term: string | null
}

interface QueryParamsState {
  template_id: string
  term: string
  status: string
}

function useDraftsQueryParams() {
  const location = useLocation()
  const history = useHistory()

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return {
      template_id: searchParams.get('template_id') || '',
      term: searchParams.get('term') || '',
      status: searchParams.get('status') || '',
    }
  }, [location.search])

  const updateQueryParams = useCallback(
    (newParams: Partial<QueryParamsState>) => {
      const searchParams = new URLSearchParams(location.search)

      if (newParams.template_id !== undefined) {
        if (newParams.template_id) {
          searchParams.set('template_id', newParams.template_id)
        } else {
          searchParams.delete('template_id')
        }
      }

      if (newParams.term !== undefined) {
        if (newParams.term) {
          searchParams.set('term', newParams.term)
        } else {
          searchParams.delete('term')
        }
      }

      if (newParams.status !== undefined) {
        if (newParams.status) {
          searchParams.set('status', newParams.status)
        } else {
          searchParams.delete('status')
        }
      }

      const newSearch = searchParams.toString()
      const currentSearch = location.search.slice(1)

      if (newSearch !== currentSearch) {
        history.replace({
          pathname: location.pathname,
          search: newSearch,
        })
      }
    },
    [history, location.pathname, location.search],
  )

  const selectedStatusFilters = useMemo(() => {
    return queryParams.status
      ? (queryParams.status.split(',').filter(Boolean) as PendingRowTypeDisplayText[])
      : []
  }, [queryParams.status])

  return {
    queryParams,
    selectedStatusFilters,
    updateQueryParams,
  }
}

function filterPendingResults(
  submissions: IPendingSubmissions,
  { term = null, template_id = null, selectedStatusFilters }: FilterPendingParams,
): IPendingSubmissions {
  if (!submissions) return []

  const filterByTermsOrTemplateId = submissions.filter(draft => {
    if (template_id && term) {
      return (
        draft?.template_id === +template_id &&
        draft?.identifier?.toLowerCase().includes(term.toLowerCase())
      )
    }

    if (template_id) {
      return draft.template_id === +template_id
    }

    if (term) {
      return draft?.identifier?.toLowerCase().includes(term.toLowerCase())
    }

    return true
  })

  if (selectedStatusFilters.length > 0) {
    const groupedResults = groupBy(filterByTermsOrTemplateId, submission => submission.type)
    return selectedStatusFilters
      .reduce<IPendingSubmissions>((acc, status) => acc.concat(groupedResults[status] ?? []), [])
      .filter(v => v)
  }

  return filterByTermsOrTemplateId
}

const Drafts = () => {
  const { queryParams, selectedStatusFilters, updateQueryParams } = useDraftsQueryParams()

  const [DATASETS_PAGE_ENABLED = false, DATA_SOURCE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE, FeatureDict.SHOW_DATA_SOURCE]),
  )
  // These columns are part of the show-data-source feature flag and should replace the columns in
  // app/submissions/drafts/config.ts when the feature flag is removed
  const dataSourceColumns = [
    { label: 'Modified', type: 'date', width: '12%', lookup: 'updated_at' },
    { label: 'Name', width: '17%', lookup: 'identifier' },
    {
      label: DATASETS_PAGE_ENABLED ? 'Dataset' : 'Template',
      width: '17%',
      lookup: 'template_name',
    },
    { label: 'Rows', width: '8%', lookup: 'number_of_rows' },
    { label: 'Source', width: '17%', lookup: 'source' },
    { label: 'Status', width: '15%', lookup: 'type' },
    { label: 'Actions', width: '14%', lookup: 'draft' },
  ]
  const { state } = useContext(PendingContext)
  const { redirectPath, handleRowClick, params } = state
  const { markDraftsAsSeen } = useContext(submissionContext)

  const { pendingSubmissions, isLoading, invalidateGlobalPending } = useGlobalPendingSubmissions()

  const filteredPending = useMemo(() => {
    return filterPendingResults(pendingSubmissions || [], {
      term: queryParams.term || null,
      template_id: queryParams.template_id || null,
      selectedStatusFilters,
    })
  }, [pendingSubmissions, selectedStatusFilters, queryParams])

  const handleChangeStatusFilter = (newValues: PendingRowTypeDisplayText[]) => {
    updateQueryParams({
      ...queryParams,
      status: newValues.length ? newValues.join(',') : '',
    })
  }

  const handleSubmissionNameSearch = (term: string) => {
    updateQueryParams({ ...queryParams, term })
  }

  const handleTemplateSearch = (templateId: string) => {
    updateQueryParams({ ...queryParams, template_id: templateId })
  }

  useEffect(() => {
    return () => {
      markDraftsAsSeen()
    }
  }, [])

  const emptyStateTitle = 'No Pending Submissions Found'

  const columnsToUse = DATA_SOURCE_ENABLED ? dataSourceColumns : getColumns(DATASETS_PAGE_ENABLED)

  const rows = filteredPending

  if (redirectPath) {
    return <Redirect push to={redirectPath} />
  }

  if (isLoading) {
    return (
      <MotionDivContainer>
        <pendingStyles.LoadingContainer>
          <CircularProgress color="primary" />
        </pendingStyles.LoadingContainer>
      </MotionDivContainer>
    )
  }

  return (
    <MotionDivContainer>
      <div css={pendingStyles.flexColumn}>
        <Toolbar
          templateSearchProps={{
            onSelected: handleTemplateSearch,
            selected: queryParams.template_id,
          }}
          pendingStatusSearchProps={{
            isDisabled: false,
            onSelected: handleChangeStatusFilter,
            options: STATUS_FILTER_OPTIONS,
            selected: selectedStatusFilters,
          }}
          searchProps={{
            onChange: event => {
              handleSubmissionNameSearch(event.target.value)
            },
            defaultValue: params.term,
          }}
        />
        <pendingStyles.FlexPaper css={pendingStyles.flexColumn}>
          <pendingStyles.TableContainer>
            {isLoading ? (
              <pendingStyles.LoadingContainer>
                <CircularProgress color="primary" />
              </pendingStyles.LoadingContainer>
            ) : (
              <CvsTable
                columns={columnsToUse}
                headerRowPadding={50}
                noAdd
                noPagination
                keyField="id"
                orderBy="updated_at"
                sortOrder="desc"
                rows={rows as never[]}
                emptyStateMessage={{
                  icon: (
                    <Fab css={pendingStyles.emptyStateFab} disabled>
                      <FileCopyOutlinedIcon css={pendingStyles.emptyStateIcon} />
                    </Fab>
                  ),
                  title: emptyStateTitle,
                }}
              >
                {({ row, index }) => (
                  <PendingTableRow
                    index={index}
                    row={row}
                    handleRowClick={handleRowClick}
                    invalidateQueryCache={invalidateGlobalPending}
                    paddingLeftFirstCell={50}
                    dataSourceEnabled={DATA_SOURCE_ENABLED}
                  />
                )}
              </CvsTable>
            )}
          </pendingStyles.TableContainer>
        </pendingStyles.FlexPaper>
      </div>
    </MotionDivContainer>
  )
}

export const DraftsWithProvider = () => (
  <PendingContextProvider>
    <Drafts />
  </PendingContextProvider>
)
